'use client';

import React, {
	ChangeEvent,
	FormEvent,
	forwardRef,
	RefObject,
	useCallback,
	useEffect,
	useState,
} from 'react';
import cn from 'classnames';
import { trackEvent } from '@/helpers/heap/trackEvent';

import { useFocusTrap } from '@/hooks/useFocusTrap';
import { setHeapPropsClientSide } from '@/helpers/heap/setHeapPropsClientSide';
import profiler from '@/helpers/userProfile/profiler';

import Citations from '../Citations';
import ConversationWrapper from '../ConversationWrapper';
import DialogTopBar from './DialogTopBar';
import DialogFormSection from './DialogFormSection';

const STORAGE_KEY = 'ai-history';

interface SearchBoxProps {
	testId?: string;
	onClose: () => void;
	navDarkVariant: boolean;
}

interface ConversationItem {
	question: string;
	answer: {
		completion: string;
		attributions: SavedCitation[];
	};
}

export type SavedCitation = {
	text: string;
	uri: string;
};
interface AttributionsProps {
	generatedResponsePart?: {
		textResponsePart?: {
			text: string;
		};
	};
	retrievedReferences?: ResponseCitation[];
}

interface ResponseCitation {
	content?: {
		text: string;
	};
	metadata?: {
		'x-amz-bedrock-kb-data-source-id'?: string;
		'x-amz-bedrock-kb-source-uri'?: string;
	};
}

const SearchBox = forwardRef<HTMLDialogElement, SearchBoxProps>(
	(props: SearchBoxProps, dialogRef) => {
		const { testId = 'SearchBox', onClose, navDarkVariant } = props;

		const [userInput, setUserInput] = useState<string>('');
		const [showError, setShowError] = useState<boolean>(false);
		const [gettingData, setGettingData] = useState<boolean>(false);
		const [conversation, setConversation] = useState<ConversationItem[]>([]);
		const [errorMessage, setErrorMessage] = useState<string>(
			'There was an error processing your request. Please try again later.',
		);
		const [userProfile] = profiler();

		// These two will help us get focus trap for a good accessibility experience
		useFocusTrap(dialogRef as RefObject<HTMLDialogElement>, true);

		// This is definitely AI, it suggest I make it like this in case there is an issue with the storage
		const parseStoredConversation = useCallback((): ConversationItem[] => {
			try {
				const storedConversation = sessionStorage.getItem(STORAGE_KEY);
				if (!storedConversation) {
					return [];
				}

				const parsed = JSON.parse(storedConversation);
				return Array.isArray(parsed) ? parsed : [];
			} catch (error) {
				return [];
			}
		}, []);

		// Load conversation history when component opens
		useEffect(() => {
			const parsedHistory = parseStoredConversation();
			setConversation(parsedHistory);
		}, [parseStoredConversation]);

		const onChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
			e.preventDefault();
			setUserInput(e.target.value);
			if (e.target.value.trim() === '') {
				setShowError(false);
			}
		};

		// Extract the citation processing into its own function
		const processCitations = useCallback(
			(attributions: AttributionsProps[]): SavedCitation[] => {
				const filteredAttributions: SavedCitation[] = [];

				if (!attributions || !Array.isArray(attributions)) {
					return filteredAttributions;
				}

				attributions.forEach((attribution: AttributionsProps) => {
					if (
						!attribution.retrievedReferences ||
						!Array.isArray(attribution.retrievedReferences)
					) {
						return;
					}

					attribution.retrievedReferences.forEach(
						(reference: ResponseCitation) => {
							if (
								!reference.metadata ||
								!reference.metadata['x-amz-bedrock-kb-source-uri'] ||
								!reference.content ||
								!reference.content.text
							) {
								return;
							}

							const uri = reference.metadata['x-amz-bedrock-kb-source-uri'];
							const text = reference.content.text;

							// Avoid duplicates
							const isDuplicate = filteredAttributions.some(
								(existing) => existing.uri === uri || existing.text === text,
							);

							if (!isDuplicate) {
								filteredAttributions.push({ text, uri });
							}
						},
					);
				});

				return filteredAttributions;
			},
			[],
		);

		const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
			e.preventDefault();

			const trimmedUserInput = userInput.trim();

			if (trimmedUserInput === '') {
				setErrorMessage('Please enter a question before submitting.');
				setShowError(true);
				return;
			}

			setShowError(false);
			setGettingData(true);

			try {
				const response = await fetch('/api/bedrock-agent', {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						userInput: trimmedUserInput,
						sessionId: userProfile.mtaSid,
					}),
				});

				if (!response.ok) {
					throw new Error('Failed to get response from AI');
				}

				const data = await response.json();

				if (data.answer) {
					const filteredAttributions = processCitations(
						data.answer.attributions,
					);

					const newConversationItem: ConversationItem = {
						question: trimmedUserInput,
						answer: {
							completion: data.answer.completion || '',
							attributions: filteredAttributions,
						},
					};

					// Track successful AI response
					trackEvent('AI Response Received', {
						'Question Text': trimmedUserInput,
						Response: data.answer.completion || '',
						Citations:
							filteredAttributions
								?.map((citation) => citation.uri)
								.join('| ') || '',
						'Question Number': (conversation.length + 1).toString(),
					});

					// Set user properties for ongoing session tracking
					setHeapPropsClientSide({
						'Last AI Question': trimmedUserInput,
						'Total AI Questions': (conversation.length + 1).toString(),
						'AI Session Active': 'true',
					});

					setConversation((prevConvo) => {
						const updatedConversation = [...prevConvo, newConversationItem];
						try {
							sessionStorage.setItem(
								STORAGE_KEY,
								JSON.stringify(updatedConversation),
							);
						} catch (error) {
							/* handle error */
						}
						return updatedConversation;
					});

					// Clear the input and stop loading on success
					setUserInput('');
					setGettingData(false);
				} else {
					throw new Error('Invalid response format');
				}
			} catch (error) {
				// Track API errors to help debug issues and improve error handling
				trackEvent('AI Search Error', {
					'Question Text': trimmedUserInput,
					'Error Type': 'API Response Error',
					'Question Number': (conversation.length + 1).toString(),
				});

				if (process.env.NODE_ENV !== 'production') {
					console.error('Error calling bedrock API:', error);
				}
				setErrorMessage(
					'There was an error processing your request. Please try again later.',
				);
				setShowError(true);
				setGettingData(false);
			}
		};

		const closeBox = useCallback(() => {
			trackEvent('AI Box Closed', {
				'Conversation Length': conversation.length,
				// Tracks the URIs of attributions in the last answer to analyze user interaction with links.
				'Last Answer Attributions':
					conversation.length > 0
						? conversation[conversation.length - 1].answer.attributions.map(
								(attribution) => attribution.uri,
						  )
						: [],
			});
			onClose();
		}, [onClose, conversation]);

		useEffect(() => {
			let dialogElement: HTMLDialogElement | null = null;
			if (
				dialogRef &&
				typeof dialogRef !== 'function' &&
				'current' in dialogRef
			) {
				dialogElement = dialogRef.current;
			}
			if (!dialogElement) return;

			// Track when dialog opens
			const handleDialogOpen = () => {
				if (dialogElement?.open) {
					trackEvent('AI Box Opened', {
						'Entry Point': 'Ask AI Button', // This could be passed as a prop
						'Page URL': window.location.href,
					});
				}
			};

			// Use MutationObserver to detect when dialog opens
			const dialogObserver = new MutationObserver((mutations) => {
				mutations.forEach((mutation) => {
					if (
						mutation.type === 'attributes' &&
						mutation.attributeName === 'open'
					) {
						handleDialogOpen();
					}
				});
			});

			dialogObserver.observe(dialogElement, {
				attributes: true,
				attributeFilter: ['open'],
			});

			return () => {
				dialogObserver.disconnect();
			};
		}, [dialogRef]);

		return (
			<dialog
				ref={dialogRef}
				data-testid={testId}
				aria-label="AI Search Dialog"
				aria-modal="true"
				role="dialog"
				className="hvac-container fixed inset-0 h-screen w-screen bg-transparent p-4 open:flex open:flex-col open:items-center open:justify-start open:pt-16"
			>
				<div
					className={cn(
						'absolute left-1/2 right-auto flex h-max max-h-[calc(100vh-4rem)]  w-11/12 -translate-x-1/2 flex-col rounded-3xl bg-white drop-shadow-xl md:left-auto md:right-10 md:w-full md:max-w-2xl md:translate-x-0  2xl:right-0',
						{ 'top-24 lg:top-28': navDarkVariant },
						{ 'top-17 xl:top-20': !navDarkVariant },
					)}
				>
					<DialogTopBar closeBox={closeBox} />

					<div className="relative mx-auto flex w-11/12">
						<div
							className={cn(
								conversation.length > 0
									? 'h-72 w-full overflow-hidden xl:h-124 xs:h-96'
									: 'h-full',
							)}
						>
							{showError && (
								<div className="absolute -top-4 left-1/2 z-20 w-max max-w-full -translate-x-1/2 rounded-lg bg-white px-2 py-1 text-center text-rose-bonbon drop-shadow-2xl">
									<p className="text-balance text-sm lg:text-base">
										{errorMessage}
									</p>
								</div>
							)}
							<ConversationWrapper items={conversation.length}>
								{conversation.length > 0 &&
									conversation.map((conversation, index) => (
										<div
											key={index}
											className="conversation-items mt-4 flex flex-col gap-3"
										>
											<div className="flex justify-end">
												<div className="rounded-2xl border border-lime-700 bg-lime-50 px-8 py-7 transition-all ease-in-out">
													<p className="text-right text-sm text-lime-700 lg:text-base">
														{conversation.question}
													</p>
												</div>
											</div>

											<div className="w-full rounded-2xl border border-sky-700 bg-sky-50 p-10 lg:w-11/12">
												<p className="whitespace-pre-wrap text-sm text-sky-700">
													{conversation.answer.completion.replace(
														/\n\n\n/g,
														'\n',
													)}
												</p>
												{conversation.answer.attributions.length > 0 && (
													<div className="mt-8 flex flex-col gap-2">
														{conversation.answer.attributions.map(
															(attribution) => (
																<Citations
																	key={attribution.uri}
																	attribution={attribution}
																/>
															),
														)}
													</div>
												)}
											</div>
										</div>
									))}
							</ConversationWrapper>
						</div>
					</div>

					<DialogFormSection
						gettingData={gettingData}
						userInput={userInput}
						onChangeHandler={onChangeHandler}
						onSubmit={onSubmit}
						closeBox={closeBox}
						conversationLength={conversation.length}
					/>
				</div>
			</dialog>
		);
	},
);

SearchBox.displayName = 'SearchBox';

export default SearchBox;
